<script setup lang="ts">
import { userPreferencesAsRef } from "@/globalConfig";
import { useNotificationStore } from "@/stores/notification";
import { ShieldCheckIcon } from "@heroicons/vue/24/outline";
import { useContentQuery } from "@/composables/useContentQuery";
import { computed, watch, h, type ComputedRef } from "vue";
import LModal from "@/components/form/LModal.vue";
import LButton from "@/components/button/LButton.vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { hasPendingLogin } from "@/composables/useAuthWithPrivacyPolicy";
import { isAuthPluginInstalled, useAuth } from "@/auth";

const { t } = useI18n();
// Only use the configured OIDC manager if a provider was restored at boot; otherwise
// fall back to unauthenticated + no-op logout.
const auth = isAuthPluginInstalled.value ? useAuth() : undefined;
const isAuthenticated = auth?.isAuthenticated ?? computed(() => false);
const logout = auth?.logout ?? (() => {});
const router = useRouter();
const route = useRoute();

const hidePrivacyPolicyFromQuery = computed(() => route.query.noprivacypolicy === "true");

const show = defineModel<boolean>("show");

// Set the privacy policy status to "updated" if the policy has changed and the user previously accepted it
// Content is partially synced, so this routes through HybridQuery (IndexedDB +
// API supplement). The injected { type: Content } is required for that routing.
// When VITE_PRIVACY_POLICY_ID is unset there is no policy page to seek. A
// `{ parentId: undefined }` clause serializes to `{}` over the wire, leaving the
// parentId index pinned with a publishDate sort but no parentId equality — which
// CouchDB rejects ("No index exists for this sort"). Match nothing via a
// provably-empty `$in` so HybridQuery short-circuits before any Dexie read or POST.
const privacyPolicyId = import.meta.env.VITE_PRIVACY_POLICY_ID;
const privacyPolicyArr = useContentQuery(
    () => (privacyPolicyId ? [{ parentId: privacyPolicyId }] : [{ parentId: { $in: [] } }]),
    {
        includeScheduled: false,
        limit: 1,
        // Seek by parentId; the publishDate sort is required to engage the index.
        useIndex: "content-parentId-publishDate-index",
        sort: [{ publishDate: "desc" }],
    },
);
const privacyPolicy = computed(() => privacyPolicyArr.value[0]);

// Logic for showing the "Necessary only" button
// Don't show "Necessary only" if user is trying to log in, because login requires full privacy policy acceptance
const necessaryOnlyLogic = computed(() => {
    // If there's a pending login action, don't show "Necessary only"
    if (hasPendingLogin.value) {
        return false;
    }

    if (!userPreferencesAsRef.value.privacyPolicy?.status && !isAuthenticated.value) return true;
    if (status.value == "outdated" && !isAuthenticated.value) return true;
    if (userPreferencesAsRef.value.privacyPolicy?.status == "accepted" && !isAuthenticated.value)
        return true;

    return false;
});

const modalMessageMap = {
    accepted: t("privacy_policy.modal.message_map.accepted"),
    outdated: t("privacy_policy.modal.message_map.outdated"),
    unaccepted: t("privacy_policy.modal.message_map.unaccepted"),
    necessaryOnly: t("privacy_policy.banner.message_map.necessaryOnly"),
};

const status: ComputedRef<"accepted" | "outdated" | "unaccepted" | "necessaryOnly"> = computed(
    () => {
        if (!userPreferencesAsRef.value.privacyPolicy) return "unaccepted";
        if (
            userPreferencesAsRef.value.privacyPolicy.status !== "accepted" &&
            userPreferencesAsRef.value.privacyPolicy.status !== "necessaryOnly" &&
            !isAuthenticated.value
        )
            return "unaccepted";
        if (
            privacyPolicy.value &&
            privacyPolicy.value.publishDate &&
            privacyPolicy.value.publishDate > userPreferencesAsRef.value.privacyPolicy.ts
        )
            return "outdated";
        if (
            privacyPolicy.value &&
            privacyPolicy.value.publishDate &&
            (userPreferencesAsRef.value.privacyPolicy.status == "accepted" ||
                userPreferencesAsRef.value.privacyPolicy.status == "necessaryOnly") &&
            privacyPolicy.value.publishDate > userPreferencesAsRef.value.privacyPolicy.ts
        )
            return "outdated";

        if (userPreferencesAsRef.value.privacyPolicy.status == "necessaryOnly")
            return "necessaryOnly";

        return "accepted";
    },
);

// Wait 2 seconds before showing the privacy policy banner
setTimeout(() => {
    watch(
        status,
        (status) => {
            if (hidePrivacyPolicyFromQuery.value) {
                useNotificationStore().removeNotification("privacy-policy-banner");
                return;
            }
            if (!status || (status != "accepted" && status != "necessaryOnly")) {
                useNotificationStore().addNotification({
                    id: "privacy-policy-banner",
                    type: "bottom",
                    state: "info",
                    title: () =>
                        status == "outdated"
                            ? t("privacy_policy.banner.title_map.outdated")
                            : t("privacy_policy.banner.title"),
                    description: () =>
                        status == "outdated"
                            ? t("privacy_policy.banner.message_map.outdated")
                            : t("privacy_policy.banner.message_map.unaccepted"),
                    icon: ShieldCheckIcon,
                    link: () => (show.value = true),
                    closable: false,
                    actions: () =>
                        h(
                            "div",
                            {
                                class: "flex flex-col items-stretch space-y-2 mt-2 md:flex-row md:items-center md:space-y-0 md:space-x-2",
                            },
                            [
                                // Accept Button
                                h(
                                    LButton,
                                    {
                                        variant: "primary",
                                        name: "accept",
                                        onClick: () => {
                                            userPreferencesAsRef.value.privacyPolicy = {
                                                status: "accepted",
                                                ts: Date.now(),
                                            };
                                            useNotificationStore().removeNotification(
                                                "privacy-policy-banner",
                                            );
                                        },
                                    },
                                    () => t("privacy_policy.modal.button_accept"),
                                ),
                                // Necessary Only Button (shown only when necessaryOnlyLogic is true)
                                h(
                                    LButton,
                                    {
                                        variant: "secondary",
                                        name: "necessary-only",
                                        onClick: () => {
                                            userPreferencesAsRef.value.privacyPolicy = {
                                                status: "necessaryOnly",
                                                ts: Date.now(),
                                            };
                                            useNotificationStore().removeNotification(
                                                "privacy-policy-banner",
                                            );
                                        },
                                        style: necessaryOnlyLogic.value ? {} : { display: "none" },
                                    },
                                    () => t("privacy_policy.modal.button_necessaryOnly"),
                                ),
                                // Log Out Button (shown only for authenticated users when status is outdated and previously accepted)
                                status === "outdated" &&
                                userPreferencesAsRef.value.privacyPolicy?.status === "accepted" &&
                                isAuthenticated.value
                                    ? h(
                                          LButton,
                                          {
                                              variant: "secondary",
                                              name: "deny",
                                              onClick: () => {
                                                  userPreferencesAsRef.value.privacyPolicy =
                                                      undefined;
                                                  logout();
                                                  useNotificationStore().removeNotification(
                                                      "privacy-policy-banner",
                                                  );
                                              },
                                          },
                                          () => t("privacy_policy.modal.button_logOut"),
                                      )
                                    : null,
                                // More Info Button
                                h(
                                    LButton,
                                    {
                                        variant: "secondary",
                                        name: "more-info",
                                        class: "text-nowrap",
                                        onClick: () => {
                                            router.push({
                                                name: "content",
                                                params: { slug: "privacy-policy" },
                                            });
                                        },
                                    },
                                    () => t("privacy_policy.modal.button_readMore"),
                                ),
                            ].filter(Boolean), // Remove null values from the array
                        ),
                });
                return;
            }

            useNotificationStore().removeNotification("privacy-policy-banner");
        },
        { immediate: true },
    );
}, 2000);

const shouldHidePrivacyPolicy = import.meta.env.VITE_HIDE_PRIVACY_POLICY === "true";
</script>

<template>
    <LModal
        :isVisible="(show || false || shouldHidePrivacyPolicy) && !hidePrivacyPolicyFromQuery"
        :heading="t('privacy_policy.modal.title')"
        @close="show = false"
    >
        <p class="mb-4 mt-4 text-gray-700 dark:text-slate-300">{{ modalMessageMap[status] }}</p>

        <p class="pt-1 italic text-gray-700 dark:text-slate-300"></p>

        <template #footer>
            <div class="flex justify-end space-x-2">
                <LButton
                    v-if="
                        !userPreferencesAsRef.privacyPolicy?.status ||
                        status === 'outdated' ||
                        status === 'necessaryOnly' ||
                        (userPreferencesAsRef.privacyPolicy?.status !== 'accepted' &&
                            !isAuthenticated)
                    "
                    variant="primary"
                    name="accept"
                    @click="
                        userPreferencesAsRef.privacyPolicy = {
                            status: 'accepted',
                            ts: Date.now(),
                        };
                        show = false;
                    "
                >
                    {{ t("privacy_policy.modal.button_accept") }}
                </LButton>

                <LButton
                    v-if="
                        status === 'outdated' &&
                        userPreferencesAsRef.privacyPolicy?.status === 'accepted' &&
                        isAuthenticated
                    "
                    @click="
                        userPreferencesAsRef.privacyPolicy = undefined;
                        logout();
                        useNotificationStore().removeNotification('privacy-policy-banner');
                        show = false;
                    "
                >
                    {{ t("privacy_policy.modal.button_logOut") }}
                </LButton>

                <LButton
                    v-if="necessaryOnlyLogic"
                    variant="secondary"
                    name="necessary-only"
                    @click="
                        userPreferencesAsRef.privacyPolicy = {
                            status: 'necessaryOnly',
                            ts: Date.now(),
                        };
                        show = false;
                    "
                >
                    {{ t("privacy_policy.modal.button_necessaryOnly") }}
                </LButton>
                <LButton
                    variant="secondary"
                    name="more-info"
                    @click="
                        router.push({ name: 'content', params: { slug: 'privacy-policy' } });
                        show = false;
                    "
                >
                    {{ t("privacy_policy.modal.button_readMore") }}
                </LButton>
            </div>
        </template>
    </LModal>
</template>
